import { create } from 'zustand';
import { applyAppLanguage } from '@/lib/apply-app-language';
import {
  countryFromLyrraOnePublicRegion,
  defaultLanguageForCountry,
  detectVisitorCountryCodeSync,
  storeVisitorCountryCode,
} from '@/lib/visitor-geo-locale';
import { countryToLyrraOneRegion } from '@/lib/lyrraone-market-config';
import { readStoredLyrraOneRegion } from '@/lib/lyrraone-public-region';
import {
  type Language,
  hasUserPersistedLanguage,
  resolveInitialLanguage,
} from '@/lib/language-prefs';

export type { Language } from '@/lib/language-prefs';
export {
  hasUserPersistedLanguage,
  readUserPersistedLanguage,
  resolveInitialLanguage,
} from '@/lib/language-prefs';
export { applyAppLanguage } from '@/lib/apply-app-language';

interface LanguageState {
  language: Language;
  visitorCountryCode: string | null;
  setLanguage: (lang: Language) => void;
  initializeVisitorLocale: () => Promise<void>;
}

export const useLanguageStore = create<LanguageState>((set, get) => ({
  language: resolveInitialLanguage(),
  visitorCountryCode: detectVisitorCountryCodeSync(),

  setLanguage: (lang: Language) => {
    applyAppLanguage(lang, { persist: true });
    set({ language: lang });
  },

  initializeVisitorLocale: async () => {
    let country = detectVisitorCountryCodeSync();

    try {
      const { api } = await import('@/lib/api');
      const res = await api.get<{ success?: boolean; data?: { country?: string | null } }>(
        '/public/region-hint',
      );
      const fromApi = res?.data?.country?.trim().toUpperCase();
      if (fromApi && /^[A-Z]{2}$/.test(fromApi)) {
        country = fromApi;
        storeVisitorCountryCode(fromApi);
      }
    } catch {
      /* géolocalisation optionnelle */
    }

    if (countryFromLyrraOnePublicRegion()) {
      country = countryFromLyrraOnePublicRegion();
      if (country) storeVisitorCountryCode(country);
    }

    if (!readStoredLyrraOneRegion() && country) {
      const detectedRegion = countryToLyrraOneRegion(country);
      if (detectedRegion) {
        const { usePublicRegionStore } = await import('@/stores/public-region.store');
        usePublicRegionStore.setState({ region: detectedRegion, initialized: true });
      }
    }

    set({ visitorCountryCode: country });

    if (hasUserPersistedLanguage()) return;

    const lang = defaultLanguageForCountry(country) as Language;
    if (lang === get().language) {
      const { applyDocumentLocale } = await import('@/lib/locale-direction');
      applyDocumentLocale(lang);
      return;
    }
    applyAppLanguage(lang, { persist: false });
    set({ language: lang });
  },
}));
